/**
 * Converts TextRestrictions into a regex pattern string that matches the allowed characters.
 * Builds the pattern dynamically based on the enabled restrictions.
 *
 * Supported patterns (original + new combinations):
 *
 * Letters only:
 * - lower: "a-z"
 * - upper: "A-Z"
 * - sentence: "a-zA-Z"
 * - mixed: "a-zA-Z"
 *
 * Letters + spaces:
 * - lower + spaces: "a-z "
 * - upper + spaces: "A-Z "
 * - sentence + spaces: "a-zA-Z "
 * - mixed + spaces: "a-zA-Z "
 *
 * Letters + symbols (no numbers):
 * - upper + symbols: "A-Z&,'.-"
 * - lower + symbols: "a-z&'"
 * - sentence + symbols: "a-zA-Z&'"
 * - mixed + symbols: "a-zA-Z&'"
 *
 * Letters + spaces + symbols (no numbers):
 * - upper + spaces + symbols: "A-Z &'"
 * - lower + spaces + symbols: "a-z &'"
 * - sentence + spaces + symbols: "a-zA-Z &'"
 * - mixed + spaces + symbols: "a-zA-Z &'"
 *
 * Letters + numbers:
 * - mixed + numbers: "a-zA-Z0-9"
 * - upper + numbers: "A-Z0-9"
 * - lower + numbers: "a-z0-9"
 * - sentence + numbers: "a-zA-Z0-9"
 *
 * Letters + numbers + spaces:
 * - mixed + numbers + spaces: "a-zA-Z0-9 "
 * - upper + numbers + spaces: "A-Z0-9 "
 * - lower + numbers + spaces: "a-z0-9 "
 * - sentence + numbers + spaces: "a-zA-Z0-9 "
 *
 * Letters + numbers + symbols (no spaces):
 * - mixed + numbers + symbols: "a-zA-Z0-9&"
 * - upper + numbers + symbols: "A-Z0-9&'"
 * - lower + numbers + symbols: "a-z0-9&'"
 * - sentence + numbers + symbols: "a-zA-Z0-9&'"
 *
 * Letters + numbers + spaces + symbols:
 * - mixed + numbers + spaces + symbols: "a-zA-Z0-9\\s!@#$%^&*()_+\\-=\\[\\]{};'\"\\\\|,.<>?\\/~`"
 * - upper + numbers + spaces + symbols: "A-Z0-9\\s./&'"
 * - lower + numbers + spaces + symbols: "a-z0-9\\s./&'"
 * - sentence + numbers + spaces + symbols: "a-zA-Z0-9\\s./&'"
 *
 * Numbers only:
 * - numbers: "0-9"
 *
 * @param restrictions - The text restrictions to convert
 * @returns A regex pattern string matching the allowed characters
 */
export const getRegexFromRestrictions = (restrictions) => {
  const parts = [];

  // Handle letters based on text case.
  if (restrictions.allowLetters) {
    const textCase = restrictions.allowedTextCase || 'MIXED';

    switch (textCase) {
      case 'LOWER':
        parts.push('a-z');
        break;
      case 'UPPER':
        parts.push('A-Z');
        break;
      case 'SENTENCE':
        parts.push('a-zA-Z');
        break;
      case 'MIXED':
      default:
        parts.push('a-zA-Z');
        break;
    }
  }

  // Handle numbers.
  if (restrictions.allowNumbers) {
    parts.push('0-9');
  }

  // Handle spaces and symbols.
  // When both numbers and symbols are present, \s is included in the symbol pattern.
  const hasNumbers = restrictions.allowNumbers;
  const hasSymbols = restrictions.allowSymbols;
  const hasSpaces = restrictions.allowSpaces;
  const includeSpaceInSymbols = hasNumbers && hasSymbols && hasSpaces;

  // Handle symbols.
  if (hasSymbols) {
    const textCase = restrictions.allowedTextCase || 'MIXED';

    // Determine symbol set based on context.
    if (textCase === 'MIXED' && hasNumbers && hasSpaces) {
      // Full symbol set for mixed case with numbers and spaces (includes \s).
      parts.push('\\s!@#$%^&*()_+\\-=\\[\\]{};\'\"\\\\|,.<>?\\/~`');
    } else if (hasNumbers && hasSpaces) {
      // Medium symbol set for other text cases with numbers and spaces (includes \s).
      // This covers UPPER and SENTENCE cases.
      parts.push("\\s./&'");
    } else if (textCase === 'MIXED' && hasNumbers && !hasSpaces) {
      // Simple symbol for mixed case with numbers but no spaces.
      parts.push('&');
    } else if (textCase === 'UPPER') {
      parts.push("&,'.-");
    } else {
      // Default symbol set for cases without numbers or with spaces but no numbers.
      // This covers: UPPER, LOWER, SENTENCE, MIXED with no numbers.
      parts.push("&'");
    }
  }

  // Handle spaces (only when NOT included in symbol pattern).
  // Spaces are included in symbol pattern when: numbers + symbols + spaces all present.
  if (hasSpaces && !includeSpaceInSymbols) {
    parts.push(' ');
  }

  // Return the combined pattern or default fallback.
  return parts.length > 0 ? parts.join('') : 'A-Za-z';
};
